// Wave 14+ / Rapha Fit Track D — Virtual try-on.
//
// Renders the rider's body hull as an interactive 3D mesh with the
// chosen garment's color applied across its coverage Y-range
// (skin-tone fallback elsewhere). Sister component to CompressionMap
// — shares the same scene shape but the per-vertex color comes from
// the garment's coverage map instead of the compression series.
//
// "Drape" is a strong word for what this does; it's a per-vertex
// recolor along the height ranges the garment covers. Good enough to
// communicate fit + brand at a glance without a real cloth sim.

import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import * as THREE from 'three';
import { useTranslation } from 'react-i18next';
import { colors } from '../theme';

interface CatalogSize {
  name: string;
}

interface CatalogGarment {
  id: string;
  category: string;
  name: string;
  sizes: CatalogSize[];
  fit_offset_pct: number;
  try_on_color?: string | null;
  coverage_y_lo_ratio?: number | null;
  coverage_y_hi_ratio?: number | null;
}

interface HullMesh {
  positions: Float32Array;
  indices: Uint16Array | Uint32Array;
  normals: Float32Array;
}

interface Props {
  garmentId: string;
  size: string;
  hullMesh: HullMesh | null;
  riderHeightCm: number | null;
  onChangeSize: (size: string) => void;
  onClose: () => void;
}

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

const SKIN_TONE: [number, number, number] = [0.78, 0.68, 0.6]; // warm grey

function hexToRgb(hex: string): [number, number, number] {
  const m = hex.replace('#', '');
  const r = parseInt(m.substring(0, 2), 16) / 255;
  const g = parseInt(m.substring(2, 4), 16) / 255;
  const b = parseInt(m.substring(4, 6), 16) / 255;
  return [r, g, b];
}

export default function TryOnView({
  garmentId,
  size,
  hullMesh,
  riderHeightCm,
  onChangeSize,
  onClose,
}: Props) {
  const { t } = useTranslation('rapha-fit');
  const [garment, setGarment] = useState<CatalogGarment | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const meshRef = useRef<THREE.Mesh | null>(null);
  const colorAttrRef = useRef<THREE.BufferAttribute | null>(null);
  const rotRef = useRef({ y: 0, dragging: false, lastX: 0 });

  // Catalog fetch — same shape CompressionMap uses.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    fetch(`${API}/fit/catalog`, {
      headers: { Authorization: `Bearer ${token ?? ''}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((catalog: CatalogGarment[]) => {
        const g = catalog.find((x) => x.id === garmentId) ?? null;
        setGarment(g);
      })
      .catch(() => {});
  }, [garmentId]);

  // Three.js scene setup (once per hull change).
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !hullMesh) return;
    const W = container.clientWidth || 320;
    const H = container.clientHeight || 480;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0e1a);
    scene.add(new THREE.AmbientLight(0xffffff, 0.95));
    const key = new THREE.DirectionalLight(0xffffff, 0.45);
    key.position.set(2, 3, 4);
    scene.add(key);

    const camera = new THREE.PerspectiveCamera(34, W / H, 0.05, 20);
    camera.position.set(0, 0.3, 2.4);
    camera.lookAt(0, 0.3, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(W, H);
    container.appendChild(renderer.domElement);

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(hullMesh.positions, 3));
    geom.setAttribute('normal', new THREE.BufferAttribute(hullMesh.normals, 3));
    geom.setIndex(new THREE.BufferAttribute(hullMesh.indices, 1));
    const nVerts = hullMesh.positions.length / 3;
    const colorArr = new Float32Array(nVerts * 3);
    for (let i = 0; i < nVerts; i++) {
      colorArr[i * 3] = SKIN_TONE[0];
      colorArr[i * 3 + 1] = SKIN_TONE[1];
      colorArr[i * 3 + 2] = SKIN_TONE[2];
    }
    const colorAttr = new THREE.BufferAttribute(colorArr, 3);
    geom.setAttribute('color', colorAttr);
    colorAttrRef.current = colorAttr;

    const mat = new THREE.MeshStandardMaterial({
      vertexColors: true,
      metalness: 0.0,
      roughness: 0.65,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geom, mat);
    mesh.frustumCulled = false;
    geom.computeBoundingBox();
    if (geom.boundingBox) {
      const center = new THREE.Vector3();
      geom.boundingBox.getCenter(center);
      mesh.position.set(-center.x, -center.y, -center.z);
    }
    scene.add(mesh);
    meshRef.current = mesh;
    rendererRef.current = renderer;

    const onDown = (e: MouseEvent) => {
      rotRef.current.dragging = true;
      rotRef.current.lastX = e.clientX;
    };
    const onUp = () => {
      rotRef.current.dragging = false;
    };
    const onMove = (e: MouseEvent) => {
      if (!rotRef.current.dragging) return;
      rotRef.current.y += (e.clientX - rotRef.current.lastX) * 0.012;
      rotRef.current.lastX = e.clientX;
    };
    renderer.domElement.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('mousemove', onMove);

    let rafId = 0;
    const tick = () => {
      mesh.rotation.y = rotRef.current.y;
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mousemove', onMove);
      renderer.domElement.removeEventListener('mousedown', onDown);
      renderer.dispose();
      geom.dispose();
      mat.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [hullMesh]);

  // Recolor whenever the garment or size changes.
  useEffect(() => {
    const colorAttr = colorAttrRef.current;
    if (!colorAttr || !hullMesh || !garment) return;
    const positions = hullMesh.positions;
    const colors_arr = colorAttr.array as Float32Array;
    const nVerts = positions.length / 3;
    const halfHeightM = (riderHeightCm ?? 175) * 0.5 * 0.01;
    const heightCm = riderHeightCm ?? 175;
    const yLo = (garment.coverage_y_lo_ratio ?? 0.50) * heightCm;
    const yHi = (garment.coverage_y_hi_ratio ?? 0.92) * heightCm;
    const garmentColor = garment.try_on_color ? hexToRgb(garment.try_on_color) : [0.5, 0.5, 0.5];
    for (let v = 0; v < nVerts; v++) {
      const worldY = positions[v * 3 + 1];
      const yCm = (worldY + halfHeightM) * 100;
      const inCoverage = yCm >= yLo && yCm <= yHi;
      const [r, g, b] = inCoverage ? garmentColor : SKIN_TONE;
      colors_arr[v * 3] = r;
      colors_arr[v * 3 + 1] = g;
      colors_arr[v * 3 + 2] = b;
    }
    colorAttr.needsUpdate = true;
  }, [garment, size, hullMesh, riderHeightCm]);

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.borderFocus}`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted }}>
            {t('tryOn.title')}
          </Typography>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: colors.text }}>
            {garment?.name ?? t('tryOn.garment')}
          </Typography>
        </Box>
        <Button onClick={onClose} variant="outlined" size="small" sx={{ color: colors.textMuted, borderColor: colors.border }}>
          {t('compression.back')}
        </Button>
      </Stack>

      <Typography sx={{ color: colors.textMuted, fontSize: '0.8125rem', mb: 2 }}>
        {t('tryOn.subtitle')}
      </Typography>

      {garment && (
        <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap' }}>
          {garment.sizes.map((s) => (
            <Chip
              key={s.name}
              label={s.name}
              onClick={() => onChangeSize(s.name)}
              sx={{
                cursor: 'pointer',
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 700,
                backgroundColor:
                  s.name === size ? 'rgba(106, 255, 69, 0.12)' : colors.surfaceElevated,
                border: `1px solid ${s.name === size ? colors.brand : colors.border}`,
                color: s.name === size ? colors.brand : colors.text,
                '&:hover': { borderColor: colors.borderFocus },
              }}
            />
          ))}
        </Stack>
      )}

      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: 480,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: colors.bg,
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
        }}
      />
      {!hullMesh && (
        <Typography sx={{ color: colors.textFaint, fontStyle: 'italic', mt: 2, fontSize: '0.8125rem' }}>
          {t('tryOn.noHull')}
        </Typography>
      )}
    </Box>
  );
}
